import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import de from '../locales/de.json';
import en from '../locales/en.json';

/** Schlüssel, unter dem die gewählte Sprache im Browser gespeichert wird. */
export const LANGUAGE_STORAGE_KEY = 'competa_lang';

/** Unterstützte Sprachen der Oberfläche. */
export const LANGUAGES = ['de', 'en'] as const;

/** Typ einer unterstützten Sprache. */
export type Language = (typeof LANGUAGES)[number];

/**
 * Ermittelt die Startsprache: gespeicherte Wahl, sonst Browsersprache, sonst Deutsch.
 * Der Zugriff auf localStorage ist abgesichert (privater Modus, gesperrte Daten).
 * @returns Die zu verwendende Sprache.
 */
export function detectLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    // Speicher nicht verfügbar: weiter mit der Browsersprache.
  }
  return window.navigator.language.toLowerCase().startsWith('en') ? 'en' : 'de';
}

/**
 * Wechselt die Sprache, speichert die Wahl (falls möglich) und setzt `lang` am Dokument.
 * @param language Die neue Sprache.
 */
export async function setLanguage(language: Language): Promise<void> {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Speichern nicht möglich: die Wahl gilt trotzdem für diese Sitzung.
  }
  document.documentElement.lang = language;
  await i18n.changeLanguage(language);
}

/**
 * Initialisiert i18next mit den deutschen und englischen Texten.
 * @param language Startsprache, standardmäßig aus `detectLanguage()`.
 * @returns Die initialisierte i18next-Instanz.
 */
export async function initI18n(language: Language = detectLanguage()): Promise<typeof i18n> {
  await i18n.use(initReactI18next).init({
    resources: { de: { translation: de }, en: { translation: en } },
    lng: language,
    fallbackLng: 'de',
    interpolation: { escapeValue: false },
  });
  document.documentElement.lang = language;
  return i18n;
}

export default i18n;
